/**
 * @file PickItemsList.tsx
 * @description Карточки позиций задания на сборку для `<lg` (phase19.14,
 * экран B шага 0; на `lg` строки рисует `PickItemsTable` — phase19.24).
 *
 * Кроме раскладки решает одну складскую задачу: любой тап по списку мимо
 * кнопок возвращает фокус в поле скана. Кладовщик листает список пальцем, и
 * без этого следующий скан уходит «в никуда» — символы сканера просто
 * теряются, потому что фокуса нет ни на одном поле.
 *
 * С phase19.23 список работает с фильтром поиска: пустой результат предлагает
 * сбросить запрос (зеркало мобильного `picking_items_list.dart`). Счётчик
 * «Показано N из M» с phase19.24 живёт в шапке секции — он общий для карточек
 * и таблицы.
 */

import { SearchX } from "lucide-react";
import type { ReactNode } from "react";

import { PickItemRow } from "./PickItemRow";
import { EMPTY_TEXT } from "./picking-item-texts";
import { Alert } from "../../ui/alert";
import { Button } from "../../ui/button";
import type {
  PickingItemsSource,
  PickingTaskItemDto,
} from "../../../lib/api-picking";

interface Props {
  /** Строки после фильтра поиска (не весь состав задания). */
  items: PickingTaskItemDto[];
  /** Текущий запрос поиска: определяет счётчик и текст пустого результата. */
  query?: string;
  /** Сбросить фильтр из пустого результата. */
  onResetFilter?: () => void;
  /**
   * Почему строк нет (phase19.23). Реестр подписей вместо одной фразы: «в
   * заказе только услуги» и «строки потерялись» требуют разных действий, а
   * общий текст «нет складских позиций» уводил кладовщика проводить пустое
   * задание по заказу с товаром.
   */
  itemsSource?: PickingItemsSource;
  /**
   * Кнопка «Пересобрать задание» для полосы «строки потерялись»: её наличие
   * (право SHOP_WMS_MANAGE + серверный `canRebuild`) задаёт и текст подсказки —
   * звать пересобрать имеет смысл только тому, кто это может.
   */
  rebuildAction?: ReactNode;
  /** false — задание закрыто или чужое: показываем факт без кнопок. */
  editable: boolean;
  busy: boolean;
  /** Строка последнего скана — подсветка. */
  scannedItemId: string | null;
  onChangeQty: (itemId: string, qty: number) => void;
  onOpenManualQty: (itemId: string) => void;
  onOpenShortage: (itemId: string) => void;
  /** Снять отметку недостачи со строки (`shortageQty: 0` на сервере). */
  onClearShortage: (itemId: string) => void;
  /** Вернуть фокус в поле скана (тап мимо кнопок, ±). */
  onReturnFocus: () => void;
}

export function PickItemsList({
  items,
  query = "",
  onResetFilter,
  itemsSource,
  rebuildAction,
  editable,
  busy,
  scannedItemId,
  onChangeQty,
  onOpenManualQty,
  onOpenShortage,
  onClearShortage,
  onReturnFocus,
}: Props) {
  const search = query.trim();

  // Фильтр ничего не нашёл — предлагаем сбросить, а не «нет складских
  // позиций»: состав задания на месте, просто запрос мимо (как в мобильном).
  if (items.length === 0 && search) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-on-surface/15 px-4 py-8 text-center">
        <SearchX className="size-8 text-on-surface-variant" aria-hidden />
        <p className="text-sm text-on-surface-variant">
          В задании нет позиций по запросу «{search}»
        </p>
        <Button variant="outline" onClick={onResetFilter}>
          Сбросить
        </Button>
      </div>
    );
  }

  if (items.length === 0) {
    // Старый сервер `itemsSource` не шлёт — держим прежнюю подпись про услуги.
    const lost = itemsSource === "empty";
    // Непривязанная позиция чинится правкой ЗАКАЗА: ни пересборка, ни
    // проведение тут не помогут — поэтому у неё свой текст и тон.
    const unlinked = itemsSource === "unlinked";
    const kind = unlinked
      ? "unlinked"
      : lost
        ? rebuildAction
          ? "empty_rebuildable"
          : "empty_locked"
        : "services_only";
    return (
      <Alert severity={unlinked ? "error" : lost ? "warning" : "info"}>
        <span className="flex flex-col items-start gap-3">
          <span>{EMPTY_TEXT[kind]}</span>
          {lost ? rebuildAction : null}
        </span>
      </Alert>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <ul
        className="flex flex-col gap-2"
        // Тап по карточке, а не по кнопке: фокус обратно в сканер. Кнопки и
        // ссылки исключаем — им фокус нужен самим (диалоги, ±).
        onClick={(e) => {
          if (!(e.target as HTMLElement).closest("button,input,a,textarea,select")) {
            onReturnFocus();
          }
        }}
      >
        {items.map((item) => (
          <PickItemRow
            key={item.id}
            id={`pick-item-${item.id}`}
            item={item}
            editable={editable}
            busy={busy}
            highlighted={scannedItemId === item.id}
            onChangeQty={(qty) => onChangeQty(item.id, qty)}
            onOpenManualQty={() => onOpenManualQty(item.id)}
            onOpenShortage={() => onOpenShortage(item.id)}
            onClearShortage={() => onClearShortage(item.id)}
          />
        ))}
      </ul>
    </div>
  );
}
